import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToHostReservations } from "../../../../redux/shortstayReservationSlice";
import { fetchAllUsers } from "../../../../redux/authSlice";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";
import { useNavigate, useParams } from "react-router-dom";

const CancelledShortReservations: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  const reservations = useSelector((state: RootState) => state.shortstayReservations.hostReservations);
  const allUsers = useSelector((state: RootState) => state.auth.allUsers);
  const listings = useSelector((state: RootState) => state.shortstays.listings);
  const loading = useSelector((state: RootState) => state.shortstayReservations.loading);

  const [fetchedListings, setFetchedListings] = useState<string[]>([]);

  useEffect(() => {
    if (userId) {
      dispatch(listenToHostReservations(userId));
      dispatch(fetchAllUsers());
    }
  }, [dispatch, userId]);

  useEffect(() => {
    reservations.forEach((res) => {
      if (!fetchedListings.includes(res.listingId)) {
        dispatch(listenToShortstayData(res.listingId));
        setFetchedListings((prev) => [...prev, res.listingId]);
      }
    });
  }, [reservations, fetchedListings, dispatch]);

  const cancelledReservations = reservations.filter(
    (res) => res.reservationStatus?.toLowerCase() === "cancelled"
  );

  const getGuest = (guestId: string) =>
    allUsers.find((u) => u.userId === guestId);

  const getListing = (listingId: string) =>
    listings.find((l) => l.listingId === listingId);

  const handleGuestClick = (guestId: string) => {
    navigate(`/about/${guestId}`);
  };

  const handleListingClick = (listingId: string) => {
    navigate(`/roomdetails/${listingId}`);
  };

  if (loading) return <p style={{ color: "#fff" }}>Loading cancelled reservations...</p>;

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        <h2 style={styles.title}>Cancelled Reservations</h2>
        {cancelledReservations.length === 0 ? (
          <p style={{ color: "#ccc" }}>No cancelled reservations found.</p>
        ) : (
          cancelledReservations.map((res) => {
            const guest = getGuest(res.guestId);
            const listing = getListing(res.listingId);
            const guestImg = guest?.profilePicture || "/avatar.png";
            const listingImg = listing?.photos?.[0] || "/fallback.jpg";

            return (
              <div key={res.id} style={styles.card}>
                <img
                  src={guestImg}
                  style={styles.profileImage}
                  alt="Guest"
                  onClick={() => handleGuestClick(res.guestId)}
                />
                <img
                  src={listingImg}
                  style={styles.listingImage}
                  alt="Listing"
                  onClick={() => handleListingClick(res.listingId)}
                />
                <div style={styles.details}>
                  <p style={styles.name}>
                    {guest?.firstName} {guest?.lastName}
                  </p>
                  <p style={styles.status}>Cancelled</p>
                  <p>
                    {new Date(res.checkIn).toLocaleDateString()} →{" "}
                    {new Date(res.checkOut).toLocaleDateString()}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CancelledShortReservations;

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    backgroundColor: "#000",
    minHeight: "100vh",
    padding: "80px 20px 60px",
    color: "#fff",
    fontFamily: "sans-serif",
    display: "flex",
    justifyContent: "center",
  },
  container: {
    width: "100%",
    maxWidth: 700,
  },
  title: {
    fontSize: "1.8rem",
    marginBottom: 20,
    textAlign: "center",
  },
  card: {
    display: "flex",
    alignItems: "center",
    background: "#111",
    border: "1px solid #333",
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    gap: 16,
  },
  profileImage: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    objectFit: "cover",
    cursor: "pointer",
    border: "2px solid #fff",
  },
  listingImage: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    objectFit: "cover",
    cursor: "pointer",
    border: "2px solid #fff",
  },
  details: {
    flex: 1,
  },
  name: {
    fontWeight: 600,
    marginBottom: 4,
  },
  status: {
    fontSize: 14,
    color: "#ff4444",
    fontWeight: 600,
  },
};
